import {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import Inventory2TwoToneIcon from '@mui/icons-material/Inventory2TwoTone';
import PersonTwoToneIcon from '@mui/icons-material/PersonTwoTone';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFeature } from '@/contexts/FeatureContext';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { tokenDecoder } from '@/utils/jwt';
import { companyProductService } from '@/data/companyProductService';
import { userService } from '@/data/userService';
import { FeatureKey } from '@/types/Licensing.interface';
import { isFeatureLocked } from '@/layouts/SidebarLayout/Sidebar/SidebarMenu/menuVisibility';
import {
  searchNavItems,
  visibleNavItems
} from '@/layouts/SidebarLayout/Sidebar/SidebarMenu/navConfig';
import {
  isPaletteShortcut,
  OPEN_COMMAND_PALETTE_EVENT,
  orderIdFromQuery
} from './commandPaletteEvents';

const REMOTE_MIN_LENGTH = 2;
const REMOTE_DEBOUNCE_MS = 250;
const REMOTE_LIMIT = 5;
const PAGE_LIMIT = 6;

type Section = 'pages' | 'orders' | 'products' | 'customers';

interface PaletteEntry {
  key: string;
  section: Section;
  primary: string;
  secondary?: string;
  path: string;
}

const SECTION_ICON: Record<Section, ReactNode> = {
  pages: <ArticleTwoToneIcon fontSize="small" />,
  orders: <ReceiptLongTwoToneIcon fontSize="small" />,
  products: <Inventory2TwoToneIcon fontSize="small" />,
  customers: <PersonTwoToneIcon fontSize="small" />
};

/**
 * Ctrl+K / ⌘K: one box to jump to a page, a product, a customer (name or
 * phone) or an order ("#1234"). Lists come from the same endpoints as the
 * pages, so the backend scopes them to the user's company and branches.
 */
const CommandPalette = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFeature } = useFeature();
  const { currentBranch } = useUserViewMode();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState<PaletteEntry[]>([]);
  const [loading, setLoading] = useState(false);
  // Late answers of an older query are dropped. (No AbortController: the API
  // client shows a toast for every cancelled request.)
  const generation = useRef(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPaletteShortcut(event)) return;
      event.preventDefault();
      setOpen((wasOpen) => !wasOpen);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpen);
    };
  }, []);

  const token = open ? localStorage.getItem('access_token') : null;
  const decoded = token ? tokenDecoder(token) : null;
  const role = decoded?.role ?? null;
  const tokenBranchId = decoded?.branchId ?? null;
  const locked = (feature: FeatureKey) =>
    isFeatureLocked(role, tokenBranchId, hasFeature, feature);

  const pages = useMemo<PaletteEntry[]>(() => {
    if (!open || !query.trim()) return [];
    return searchNavItems(
      visibleNavItems({
        role,
        tokenBranchId,
        hasFeature,
        hasBranch: Boolean(currentBranch)
      }),
      query,
      t
    )
      .slice(0, PAGE_LIMIT)
      .map((item) => ({
        key: `page:${item.path}`,
        section: 'pages',
        primary: t(item.labelKey),
        secondary: item.groupKey ? t(item.groupKey) : undefined,
        path: item.path
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, role, tokenBranchId, currentBranch, t]);

  const orderId = orderIdFromQuery(query);
  const orderEntry: PaletteEntry[] =
    open && orderId && !locked(FeatureKey.ORDERS)
      ? [
          {
            key: `order:${orderId}`,
            section: 'orders',
            primary: t('commandPalette.openOrder', { id: orderId }),
            path: `/dashboards/orders/${orderId}`
          }
        ]
      : [];

  useEffect(() => {
    const term = query.trim();
    const current = ++generation.current;
    // "#123" is only an order number; plain digits may also be part of a
    // customer's phone, so customers (and products) are still searched.
    if (
      !open ||
      term.length < REMOTE_MIN_LENGTH ||
      (term.startsWith('#') && orderIdFromQuery(term))
    ) {
      setRemote([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      const searchProducts = !locked(FeatureKey.PRODUCTS);
      const searchCustomers = !locked(FeatureKey.USERS);
      const [products, customers] = await Promise.all([
        searchProducts
          ? companyProductService
              .getAll({ search: term, limit: REMOTE_LIMIT, page: 0 })
              .then((result) => result.items ?? [])
              .catch(() => [])
          : Promise.resolve([]),
        searchCustomers
          ? userService
              .searchUsers({
                search: term,
                role: 'CUSTOMER',
                limit: REMOTE_LIMIT,
                page: 0
              })
              .then((result) => result.items ?? [])
              .catch(() => [])
          : Promise.resolve([])
      ]);
      if (current !== generation.current) return;
      setRemote([
        ...products.map(
          (product): PaletteEntry => ({
            key: `product:${product.id}`,
            section: 'products',
            primary: product.name,
            secondary: product.category?.name,
            path: `/management/company-products/${product.id}`
          })
        ),
        ...customers.map(
          (customer): PaletteEntry => ({
            key: `customer:${customer.id}`,
            section: 'customers',
            primary:
              `${customer.name ?? ''} ${customer.surname ?? ''}`.trim() ||
              `#${customer.id}`,
            secondary: customer.phone ?? undefined,
            path: `/management/users/details/${customer.id}`
          })
        )
      ]);
      setLoading(false);
    }, REMOTE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  const entries = [...orderEntry, ...pages, ...remote];

  useEffect(() => {
    setActive(0);
  }, [query]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setRemote([]);
  };

  const choose = (entry: PaletteEntry | undefined) => {
    if (!entry) return;
    close();
    navigate(entry.path);
  };

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, entries.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(entries[active]);
    }
  };

  const sections: Section[] = ['orders', 'pages', 'products', 'customers'];
  const term = query.trim();

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { alignSelf: 'flex-start', mt: '12vh' } }}
      aria-label={t('commandPalette.title')}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          autoFocus
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('commandPalette.placeholder')}
          inputProps={{ 'aria-label': t('commandPalette.placeholder') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null
          }}
        />
      </Box>
      <List
        dense
        sx={{ maxHeight: '55vh', overflowY: 'auto', pt: 0 }}
        role="listbox"
      >
        {sections.map((section) => {
          const items = entries.filter((entry) => entry.section === section);
          if (items.length === 0) return null;
          return (
            <li key={section}>
              <ul style={{ padding: 0 }}>
                <ListSubheader sx={{ lineHeight: '32px' }}>
                  {t(`commandPalette.section.${section}`)}
                </ListSubheader>
                {items.map((entry) => {
                  const index = entries.indexOf(entry);
                  return (
                    <ListItemButton
                      key={entry.key}
                      role="option"
                      aria-selected={index === active}
                      selected={index === active}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(entry)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {SECTION_ICON[section]}
                      </ListItemIcon>
                      <ListItemText
                        primary={entry.primary}
                        secondary={entry.secondary}
                      />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </List>
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {!term
            ? t('commandPalette.hint')
            : !loading && entries.length === 0
              ? t('commandPalette.empty')
              : t('commandPalette.keys')}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
